import { computed, ref, watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import type { HoleCards, Player, PlayerId, RoomState } from '@acepoker/contract/acepoker'

interface UseSeatCardGroupParams {
  isPlayerCardsVisible: Ref<boolean>
  loggedInPlayer: ComputedRef<Player | null>
  numCardsPerPlayer?: number
  roomState: Ref<RoomState>
  winnerIds: Ref<PlayerId[]>
}
interface UseSeatCardGroupReturn {
  isDealing: Ref<boolean>
  visiblePlayerCards: Ref<HoleCards>
}

const CARD_ANIMATION_DELAY = 100

const symbolToRank = {
  '2': 0,
  '3': 1,
  '4': 2,
  '5': 3,
  '6': 4,
  '7': 5,
  '8': 6,
  '9': 7,
  T: 8,
  J: 9,
  Q: 10,
  K: 11,
  A: 12
} as const

const symbolToSuit = {
  s: 0,
  h: 1,
  d: 2,
  c: 3
} as const

type Rank = keyof typeof symbolToRank
type Suit = keyof typeof symbolToSuit
type Card = `${Rank}${Suit}`

const sortCards = (cards: (Card | null)[] | null): (Card | null)[] | null => {
  if (!cards) return null

  return [...cards].sort((a, b) => {
    if (a === null || b === null) return 0

    const [rankA, suitA] = a.split('') as [Rank, Suit]
    const [rankB, suitB] = b.split('') as [Rank, Suit]

    const suitDiff = symbolToSuit[suitA] - symbolToSuit[suitB]
    return suitDiff !== 0 ? suitDiff : symbolToRank[rankA] - symbolToRank[rankB]
  })
}

const delay = (ms = CARD_ANIMATION_DELAY) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

const isAllCardsFilled = (cardsMap: HoleCards | undefined): boolean => {
  if (!cardsMap) return false

  return Object.values(cardsMap).every(
    (cards) => Array.isArray(cards) && cards.length > 0 && cards.every((card) => card !== null)
  )
}

export const useSeatCardGroup = (data: UseSeatCardGroupParams): UseSeatCardGroupReturn => {
  const { isPlayerCardsVisible, loggedInPlayer, numCardsPerPlayer = 2, roomState, winnerIds } = data

  const visiblePlayerCards = ref<HoleCards>({})
  const isDealing = ref(false)

  const holeCards = computed((): HoleCards | undefined => {
    return roomState.value.hand?.holeCards
  })

  const shouldRevealCard = (playerId: PlayerId): boolean => {
    const isLearningMode = roomState.value.gameConfig?.learningMode ?? false
    const isLoggedInPlayer = playerId === loggedInPlayer.value?.playerId

    return (isLearningMode && isPlayerCardsVisible.value) || isLoggedInPlayer
  }
  const clearVisiblePlayerCards = () => {
    visiblePlayerCards.value = {}
  }

  const dealCards = async (isNewHandStarting: boolean, showWinners = false) => {
    if (!holeCards.value) return

    clearVisiblePlayerCards()
    isDealing.value = isNewHandStarting

    if (isNewHandStarting) {
      await delay()
    }

    for (let i = 0; i < numCardsPerPlayer; i++) {
      for (const [playerId, cards] of Object.entries(holeCards.value)) {
        if (!visiblePlayerCards.value[playerId]) {
          visiblePlayerCards.value[playerId] = []
        }
        const sortedCards = sortCards(cards)

        if (!sortedCards) {
          visiblePlayerCards.value[playerId] = null
          continue
        }

        const card = showWinners
          ? sortedCards[i]
          : shouldRevealCard(playerId)
            ? sortedCards[i]
            : null
        visiblePlayerCards.value[playerId]?.push(card)

        if (isNewHandStarting) {
          await delay()
        }
      }
    }

    isDealing.value = false
  }

  watch(
    holeCards,
    (newCards) => {
      if (!newCards) return
      dealCards(!!(isAllCardsFilled(newCards) && roomState.value.isNewHandStarting))
    },
    {
      immediate: true
    }
  )
  watch(
    winnerIds,
    (newWinnerIds, oldWinnerIds) => {
      if (newWinnerIds.length && oldWinnerIds && oldWinnerIds.length === 0) {
        dealCards(false, true)
      }
    },
    { immediate: true }
  )

  return {
    isDealing,
    visiblePlayerCards
  }
}
